import { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyPayment = {
  invoice_reference: '',
  client: '',
  amount: 0,
  method: 'virement',
  status: 'recu',
  payment_date: '',
  notes: '',
};

const AdminPaiements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formState, setFormState] = useState(emptyPayment);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setItems(await adminGet('/admin/payments'));
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormState(emptyPayment);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormState({ ...emptyPayment, ...item });
    setModalOpen(true);
  };

  const handleChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formState, amount: Number(formState.amount || 0) };
      if (editingItem) {
        await adminPut(`/admin/payments/${editingItem.id}`, payload);
        toast.success('Paiement mis à jour');
      } else {
        await adminPost('/admin/payments', payload);
        toast.success('Paiement créé');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await adminDelete(`/admin/payments/${itemId}`);
      toast.success('Paiement supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />
      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Paiements</h2>
            <p className="crm-section-subtitle">
              Suivez les règlements reçus, attendus ou en rapprochement.
            </p>
          </div>
        </div>

        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Facture</th>
                <th>Montant</th>
                <th>Mode</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="7"><div className="crm-empty">Aucun paiement enregistré.</div></td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.client}</td>
                    <td>{item.invoice_reference || 'Sans facture'}</td>
                    <td>{Math.round(Number(item.amount || 0)).toLocaleString('fr-FR')} €</td>
                    <td>{item.method}</td>
                    <td>{item.status}</td>
                    <td>{item.payment_date}</td>
                    <td>
                      <div className="crm-table__actions">
                        <button type="button" className="crm-action-link" onClick={() => openEditModal(item)}>Modifier</button>
                        <button type="button" className="crm-action-danger" onClick={() => handleDelete(item.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingItem ? 'Modifier un paiement' : 'Créer un paiement'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingItem ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Client</label>
              <input value={formState.client} onChange={(e) => handleChange('client', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Réf. facture</label>
              <input value={formState.invoice_reference} onChange={(e) => handleChange('invoice_reference', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Montant</label>
              <input type="number" min="0" value={formState.amount} onChange={(e) => handleChange('amount', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Mode de paiement</label>
              <select value={formState.method} onChange={(e) => handleChange('method', e.target.value)}>
                <option value="virement">Virement</option>
                <option value="cheque">Chèque</option>
                <option value="especes">Espèces</option>
                <option value="acompte">Acompte</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Statut</label>
              <select value={formState.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="recu">Reçu</option>
                <option value="attendu">Attendu</option>
                <option value="rapprochement">En rapprochement</option>
                <option value="retard">En retard</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Date</label>
              <input type="date" value={formState.payment_date} onChange={(e) => handleChange('payment_date', e.target.value)} required />
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Notes</label>
              <textarea value={formState.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminPaiements;
